import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


import ParticipantTable from '../../../components/ui/ParticipantTable';

const ParticipantManagementTab = ({
  sessionData,
  onUpdateParticipant,
  onDeleteParticipant,
  onEditParticipant,
  onBulkUpdateParticipants,
  onDownloadRoster
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  const handleEditParticipant = (participant) => {
    if (onEditParticipant) onEditParticipant(participant);
  };

  const handleDeleteParticipant = (participant) => {
    if (window.confirm(`정말로 ${participant?.name}님을 삭제하시겠습니까?`)) {
      onDeleteParticipant(participant?.id);
    }
  };

  const handleStatusChange = (participant, newStatus) => {
    onUpdateParticipant({ ...participant, status: newStatus });
  };

  const handleBulkConfirm = () => {
    if (selectedParticipants?.length === 0) {
      alert('참가자를 선택해주세요.');
      return;
    }
    
    if (window.confirm(`선택한 ${selectedParticipants?.length}명의 참가를 확정하시겠습니까?`)) {
      const base = Array.isArray(sessionData?.participants) ? sessionData.participants : [];
      const idSet = new Set(selectedParticipants);
      const nextParticipants = base.map(p => (idSet.has(p?.id) ? { ...p, status: 'confirmed' } : p));
      if (onBulkUpdateParticipants) {
        onBulkUpdateParticipants(nextParticipants);
      } else {
        selectedParticipants?.forEach(id => {
          const participant = base?.find(p => p?.id === id);
          if (participant) {
            onUpdateParticipant({ ...participant, status: 'confirmed' });
          }
        });
      }
      setSelectedParticipants([]);
      setShowBulkActions(false);
    }
  };

  const handleBulkPaymentConfirm = () => {
    if (selectedParticipants?.length === 0) {
      alert('참가자를 선택해주세요.');
      return;
    }
    
    if (window.confirm(`선택한 ${selectedParticipants?.length}명의 결제를 확인하시겠습니까?`)) {
      const base = Array.isArray(sessionData?.participants) ? sessionData.participants : [];
      const idSet = new Set(selectedParticipants);
      const nextParticipants = base.map(p => (idSet.has(p?.id) ? { ...p, payment: 'paid' } : p));
      if (onBulkUpdateParticipants) {
        onBulkUpdateParticipants(nextParticipants);
      } else {
        selectedParticipants?.forEach(id => {
          const participant = base?.find(p => p?.id === id);
          if (participant) {
            onUpdateParticipant({ ...participant, payment: 'paid' });
          }
        });
      }
      setSelectedParticipants([]);
      setShowBulkActions(false);
    }
  };

  const toggleParticipantSelection = (participantId) => {
    setSelectedParticipants(prev => {
      if (prev?.includes(participantId)) {
        return prev?.filter(id => id !== participantId);
      }
      return [...prev, participantId];
    });
  };

  const toggleSelectAll = () => {
    if (selectedParticipants?.length === sessionData?.participants?.length) {
      setSelectedParticipants([]);
    } else {
      setSelectedParticipants(sessionData?.participants?.map(p => p?.id));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-4 md:p-6 bg-card rounded-md border border-border">
        <div>
          <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-1">
            참가자 관리
          </h3>
          <p className="text-sm caption text-muted-foreground">
            총 {sessionData?.participants?.length}명 등록 / 정원 {sessionData?.capacity}명
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="default"
            iconName="UserPlus"
            iconPosition="left"
            onClick={() => setShowBulkActions(!showBulkActions)}
          >
            일괄 작업
          </Button>
          
          <Button
            variant="default"
            size="default"
            iconName="Download"
            iconPosition="left"
            onClick={() => {
              if (onDownloadRoster) onDownloadRoster();
            }}
          >
            명단 다운로드
          </Button>
        </div>
      </div>
      {showBulkActions && (
        <div className="p-4 md:p-6 bg-accent/10 rounded-md border border-accent/20">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={selectedParticipants?.length === sessionData?.participants?.length}
                onChange={toggleSelectAll}
                className="w-5 h-5 rounded border-border"
              />
              <span className="text-sm font-medium text-foreground">
                {selectedParticipants?.length}명 선택됨
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="default"
                size="sm"
                iconName="CheckCircle"
                iconPosition="left"
                onClick={handleBulkConfirm}
                disabled={selectedParticipants?.length === 0}
              >
                참가 확정
              </Button>
              
              <Button
                variant="success"
                size="sm"
                iconName="DollarSign"
                iconPosition="left"
                onClick={handleBulkPaymentConfirm}
                disabled={selectedParticipants?.length === 0}
              >
                결제 확인
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                iconName="X"
                onClick={() => {
                  setSelectedParticipants([]);
                  setShowBulkActions(false);
                }}
              >
                취소
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="bg-card rounded-md border border-border p-4 md:p-6">
        <ParticipantTable
          participants={sessionData?.participants}
          onEdit={handleEditParticipant}
          onDelete={handleDeleteParticipant}
          onStatusChange={handleStatusChange}
          showActions={true}
          showFilters={true}
          columns={['name', 'phone', 'member', 'status', 'payment', 'registeredAt']}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 md:p-6 bg-card rounded-md border border-border">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-success/10 rounded-md flex items-center justify-center">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" />
            </div>
            <div>
              <p className="text-sm caption text-muted-foreground">확정</p>
              <p className="text-2xl font-heading font-bold text-foreground">
                {sessionData?.participants?.filter(p => p?.status === 'confirmed')?.length}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-6 bg-card rounded-md border border-border">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-warning/10 rounded-md flex items-center justify-center">
              <Icon name="Clock" size={20} color="var(--color-warning)" />
            </div>
            <div>
              <p className="text-sm caption text-muted-foreground">대기</p>
              <p className="text-2xl font-heading font-bold text-foreground">
                {sessionData?.participants?.filter(p => p?.status === 'pending')?.length}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-6 bg-card rounded-md border border-border">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-success/10 rounded-md flex items-center justify-center">
              <Icon name="DollarSign" size={20} color="var(--color-success)" />
            </div>
            <div>
              <p className="text-sm caption text-muted-foreground">결제 완료</p>
              <p className="text-2xl font-heading font-bold text-foreground">
                {sessionData?.participants?.filter(p => p?.payment === 'paid')?.length}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-6 bg-card rounded-md border border-border">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-error/10 rounded-md flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} color="var(--color-error)" />
            </div>
            <div>
              <p className="text-sm caption text-muted-foreground">결제 미완료</p>
              <p className="text-2xl font-heading font-bold text-foreground">
                {sessionData?.participants?.filter(p => p?.payment !== 'paid')?.length}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParticipantManagementTab;